/**
 * LoanCarReturnModal
 *
 * Shown BEFORE the "Vehicle Collected" confirmation when the job's booking
 * had a loan car with an OUT incident recorded but no IN yet.
 *
 * Props:
 *   jobUuid        {string}  — UUID of the job
 *   handoverStatus {object}  — HandoverStatusResponse from GET …/handover-status
 *                              { hasOut, hasIn, rego, outIncident, inIncident }
 *   onDone         {fn}      — called with { recorded: boolean } when modal closes
 */

import React, { useState, useRef } from 'react';
import {
    Car, Camera, Upload, X, RefreshCw, CheckCircle,
    AlertTriangle, FileText, Image, Trash2, ChevronDown, ChevronUp, Gauge,
} from 'lucide-react';
import axiosInstance from '../services/axiosInstance';

// ── Category config ────────────────────────────────────────────────────────────

const CATEGORIES = [
    {
        key: 'CAR_DASHBOARD_ON_VEHICLE_START',
        label: 'Dashboard Photo',
        description: 'Photo of the dashboard when vehicle is started at return',
        mandatory: true,
        icon: Camera,
    },
    {
        key: 'INSURANCE_CERTIFICATE',
        label: 'Insurance Certificate',
        description: 'Proof of supplementary insurance coverage',
        mandatory: false,
        icon: FileText,
    },
    {
        key: 'OTHER',
        label: 'Other Photos',
        description: 'Damage, fuel gauge, or general condition photos',
        mandatory: false,
        icon: Image,
    },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

const inputCls = (hasError) =>
    `w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 transition-colors ${
        hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'
    }`;

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const CATEGORY_LABELS = {
    DRIVING_LICENCE:                'Driving Licence',
    CAR_DASHBOARD_ON_VEHICLE_START: 'Dashboard (on start)',
    INSURANCE_CERTIFICATE:          'Insurance Certificate',
    OTHER:                          'Other',
};

// ── OutIncidentSummary ─────────────────────────────────────────────────────────

/**
 * Collapsible read-only display of the OUT (vehicle drop-off) incident.
 * Collapsed by default — staff can tap to review photos/notes if needed.
 */
const OutIncidentSummary = ({ incident }) => {
    const [expanded, setExpanded] = useState(false);
    const hasPhotos = (incident.imagesByCategory ?? []).some(c => c.images?.length > 0);

    return (
        <div className="rounded-xl border border-blue-200 overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors text-left"
            >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 shrink-0">
                        OUT — Handed out
                    </span>
                    <span className="text-xs text-gray-400 truncate">{formatDate(incident.recordedAt)}</span>
                    {incident.odometerReading != null && (
                        <span className="text-xs text-gray-500 flex items-center gap-1 shrink-0">
                            <Gauge size={11} className="text-gray-400" />
                            {incident.odometerReading.toLocaleString()} km
                        </span>
                    )}
                </div>
                {expanded
                    ? <ChevronUp size={14} className="text-blue-400 shrink-0 ml-2" />
                    : <ChevronDown size={14} className="text-blue-400 shrink-0 ml-2" />
                }
            </button>

            {expanded && (
                <div className="px-4 pb-4 pt-3 space-y-3 bg-white border-t border-blue-100">
                    <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                        <div className="flex items-center gap-1.5">
                            <Gauge size={14} className="text-gray-400" />
                            <span className="font-medium">
                                {incident.odometerReading != null
                                    ? `${incident.odometerReading.toLocaleString()} km`
                                    : '—'}
                            </span>
                        </div>
                        {incident.insuranceTaken && (
                            <div className="flex items-center gap-1.5">
                                <CheckCircle size={14} className="text-emerald-500" />
                                <span>Insurance taken</span>
                            </div>
                        )}
                    </div>

                    {incident.notes && (
                        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 leading-relaxed">
                            {incident.notes}
                        </p>
                    )}

                    {hasPhotos ? (
                        <div className="space-y-2">
                            {(incident.imagesByCategory ?? []).map(cat =>
                                cat.images?.length > 0 ? (
                                    <div key={cat.category}>
                                        <p className="text-xs font-medium text-gray-500 mb-1.5">
                                            {CATEGORY_LABELS[cat.category] ?? cat.category}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {cat.images.map((img, idx) => (
                                                <a
                                                    key={idx}
                                                    href={img.presignedUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block w-14 h-14 rounded-lg overflow-hidden border border-gray-200 hover:opacity-80 transition-opacity shrink-0"
                                                >
                                                    <img
                                                        src={img.presignedUrl}
                                                        alt={`${CATEGORY_LABELS[cat.category] ?? cat.category} ${idx + 1}`}
                                                        className="w-full h-full object-cover"
                                                        onError={e => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                ) : null
                            )}
                        </div>
                    ) : (
                        <p className="text-xs text-gray-400 italic">No photos recorded for OUT handover.</p>
                    )}
                </div>
            )}
        </div>
    );
};

// ── PhotoUploadRow ─────────────────────────────────────────────────────────────

const PhotoUploadRow = ({ category, images, onUpload, onRemove }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [expanded, setExpanded] = useState(true);
    const fileRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';

        const formData = new FormData();
        formData.append('photo', file);

        setUploading(true);
        setUploadError(null);
        try {
            const res = await axiosInstance.post(
                `/loan-car-incidents/upload-photo?category=${category.key}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            onUpload(category.key, res.data.s3Key, file.name);
        } catch (err) {
            setUploadError(err?.response?.data?.message || 'Upload failed — please try again');
        } finally {
            setUploading(false);
        }
    };

    const Icon = category.icon;

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            {/* Header */}
            <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    images.length > 0 ? 'bg-sky-100' : category.mandatory ? 'bg-red-50' : 'bg-gray-100'
                }`}>
                    <Icon size={15} className={
                        images.length > 0 ? 'text-sky-600' : category.mandatory ? 'text-red-400' : 'text-gray-400'
                    } />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800">{category.label}</span>
                        {category.mandatory && (
                            <span className="text-xs font-medium text-red-500">Required</span>
                        )}
                        {images.length > 0 && (
                            <span className="text-xs font-medium text-sky-600 bg-sky-50 px-1.5 py-0.5 rounded-full">
                                {images.length} photo{images.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{category.description}</p>
                </div>
                {expanded ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
            </button>

            {/* Body */}
            {expanded && (
                <div className="px-4 py-3 space-y-2 bg-white">
                    {/* Uploaded thumbnails */}
                    {images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                            {images.map((img, i) => (
                                <div
                                    key={img.s3Key}
                                    className="relative flex items-center gap-1.5 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5 text-xs text-sky-700"
                                >
                                    <CheckCircle size={11} className="text-sky-500 shrink-0" />
                                    <span className="max-w-[140px] truncate font-medium">{img.filename}</span>
                                    <button
                                        type="button"
                                        onClick={() => onRemove(category.key, img.s3Key)}
                                        className="ml-1 text-sky-400 hover:text-red-500 transition-colors"
                                        title="Remove"
                                    >
                                        <X size={11} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Upload button */}
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-200 rounded-lg text-xs text-gray-500 hover:border-sky-300 hover:text-sky-600 hover:bg-sky-50 transition-colors w-full justify-center disabled:opacity-50"
                    >
                        {uploading
                            ? <><RefreshCw size={12} className="animate-spin" /> Uploading…</>
                            : <><Upload size={12} /> {images.length > 0 ? 'Add another photo' : 'Upload photo'}</>
                        }
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleFileChange}
                    />
                    {uploadError && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                            <AlertTriangle size={11} /> {uploadError}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// ── LoanCarReturnModal ─────────────────────────────────────────────────────────

const LoanCarReturnModal = ({ jobUuid, handoverStatus, onDone }) => {
    const { rego: loanCarRego, outIncident } = handoverStatus;
    const [odometer, setOdometer]   = useState('');
    const [notes, setNotes]         = useState('');
    // imagesByCategory: { [categoryKey]: [{ s3Key, filename }] }
    const [imagesByCategory, setImagesByCategory] = useState(
        Object.fromEntries(CATEGORIES.map(c => [c.key, []]))
    );
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    // ── Photo management ────────────────────────────────────────────────────

    const handleUpload = (categoryKey, s3Key, filename) => {
        setImagesByCategory(prev => ({
            ...prev,
            [categoryKey]: [...prev[categoryKey], { s3Key, filename }],
        }));
        // Clear category-level validation error if it was set
        if (fieldErrors[categoryKey]) {
            setFieldErrors(e => ({ ...e, [categoryKey]: null }));
        }
    };

    const handleRemove = (categoryKey, s3Key) => {
        setImagesByCategory(prev => ({
            ...prev,
            [categoryKey]: prev[categoryKey].filter(i => i.s3Key !== s3Key),
        }));
    };

    // ── Validation ──────────────────────────────────────────────────────────

    const validate = () => {
        const errors = {};

        if (!odometer || isNaN(Number(odometer)) || Number(odometer) < 0) {
            errors.odometer = 'A valid odometer reading is required';
        }

        const mandatoryCategories = CATEGORIES.filter(c => c.mandatory);
        for (const cat of mandatoryCategories) {
            if (imagesByCategory[cat.key].length === 0) {
                errors[cat.key] = `At least one ${cat.label.toLowerCase()} photo is required`;
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ── Submit ──────────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        if (!validate()) return;

        // Build imagesByCategory payload in the format the backend expects:
        // [{ category: "CAR_DASHBOARD_ON_VEHICLE_START", images: [{ s3Key }] }]
        const imagesPayload = CATEGORIES
            .map(cat => ({
                category: cat.key,
                images: imagesByCategory[cat.key].map(i => ({ s3Key: i.s3Key })),
            }))
            .filter(cat => cat.images.length > 0);

        const payload = {
            odometerReading: parseInt(odometer, 10),
            notes: notes.trim() || null,
            imagesByCategory: imagesPayload,
        };

        setSubmitting(true);
        setSubmitError(null);
        try {
            await axiosInstance.post(
                `/loan-car-incidents/job/${jobUuid}/return`,
                payload
            );
            onDone({ recorded: true });
        } catch (err) {
            const msg = err?.response?.data?.message
                || err?.response?.data
                || 'Failed to record loan car return. Please try again.';
            setSubmitError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────

    const dashboardImages = imagesByCategory['CAR_DASHBOARD_ON_VEHICLE_START'];
    const allPhotosCount  = Object.values(imagesByCategory).reduce((n, imgs) => n + imgs.length, 0);

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg flex flex-col max-h-[92vh] shadow-2xl">

                {/* ── Header ── */}
                <div className="px-6 py-4 border-b shrink-0">
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 bg-sky-100 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                            <Car size={20} className="text-sky-600" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-bold text-gray-900">Record Loan Car Return</h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Loan vehicle{' '}
                                <span className="font-semibold text-gray-700 font-mono">{loanCarRego}</span>
                                {' '}is being returned. Please complete the IN handover.
                            </p>
                        </div>
                    </div>

                    {/* Progress indicator */}
                    <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                        <div className={`flex items-center gap-1 ${odometer ? 'text-emerald-600 font-medium' : ''}`}>
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${odometer ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                {odometer && <CheckCircle size={10} className="text-white" />}
                            </div>
                            Odometer
                        </div>
                        <div className="flex-1 h-px bg-gray-200" />
                        <div className={`flex items-center gap-1 ${dashboardImages.length > 0 ? 'text-emerald-600 font-medium' : ''}`}>
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${dashboardImages.length > 0 ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                {dashboardImages.length > 0 && <CheckCircle size={10} className="text-white" />}
                            </div>
                            Dashboard photo
                        </div>
                        <div className="flex-1 h-px bg-gray-200" />
                        <div className={`flex items-center gap-1 ${allPhotosCount > 0 && odometer ? 'text-emerald-600 font-medium' : 'text-gray-400'}`}>
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${allPhotosCount > 0 && odometer ? 'bg-emerald-500 border-emerald-500' : 'border-gray-200'}`}>
                                {allPhotosCount > 0 && odometer && <CheckCircle size={10} className="text-white" />}
                            </div>
                            Ready to submit
                        </div>
                    </div>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

                    {/* OUT summary — collapsed by default, tap to review */}
                    {outIncident && (
                        <OutIncidentSummary incident={outIncident} />
                    )}

                    {/* Submit error */}
                    {submitError && (
                        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                            <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700 leading-snug">{submitError}</p>
                        </div>
                    )}

                    {/* Odometer */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                            Odometer Reading <span className="text-red-400">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                min="0"
                                max="9999999"
                                value={odometer}
                                onChange={e => {
                                    const v = e.target.value;
                                    setOdometer(v);
                                    if (fieldErrors.odometer) setFieldErrors(fe => ({ ...fe, odometer: null }));
                                }}
                                placeholder="e.g. 45000"
                                className={inputCls(!!fieldErrors.odometer) + ' pr-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'}
                                autoFocus
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                                km
                            </span>
                        </div>
                        {fieldErrors.odometer && (
                            <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertTriangle size={11} /> {fieldErrors.odometer}
                            </p>
                        )}
                    </div>

                    {/* Photo upload sections */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
                            Handover Photos
                        </label>
                        <div className="space-y-2">
                            {CATEGORIES.map(cat => (
                                <div key={cat.key}>
                                    <PhotoUploadRow
                                        category={cat}
                                        images={imagesByCategory[cat.key]}
                                        onUpload={handleUpload}
                                        onRemove={handleRemove}
                                    />
                                    {fieldErrors[cat.key] && (
                                        <p className="text-xs text-red-500 mt-1 px-1 flex items-center gap-1">
                                            <AlertTriangle size={11} /> {fieldErrors[cat.key]}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                            Return Notes <span className="text-gray-400 font-normal normal-case">(optional)</span>
                        </label>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Any damage observed, fuel level, or other notes on vehicle return…"
                            rows={3}
                            className={inputCls(false) + ' resize-none'}
                        />
                    </div>

                </div>

                {/* ── Footer ── */}
                <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={() => onDone({ recorded: false })}
                        disabled={submitting}
                        className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-white transition-colors disabled:opacity-50"
                        title="Skip loan car return — you can record it later from the incident history"
                    >
                        Skip for now
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-600 text-white rounded-xl text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 transition-colors"
                    >
                        {submitting
                            ? <><RefreshCw size={14} className="animate-spin" /> Recording return…</>
                            : <><CheckCircle size={14} /> Record Loan Car Return</>
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoanCarReturnModal;